import { t } from "i18next";
import * as React from "react";
import { Stack, Row, Col } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";

import { TSupplier } from "../../services/API/Suppliers";
import StyledCheckbox from "../StyledCheckbox/StyledCheckbox";
import DatePicker_react from "../DatePicker/DatePicker_react";

interface Filter<T> {
  OnChange(newFilter: T): void;
  currentFilter: T;
  options?: T[];
}

export interface FilterOptions {
  term: Filter<string>;
  supplier: Filter<string>;
  category: Filter<string>;
  startDateBegin: Filter<Date | null>;
  startDateEnd: Filter<Date | null>;
  startReportDateBegin: Filter<Date | null>;
  startReportDateEnd: Filter<Date | null>;
  endReportDateBegin: Filter<Date | null>;
  endReportDateEnd: Filter<Date | null>;
  terminateDateBegin: Filter<Date | null>;
  terminateDateEnd: Filter<Date | null>;
  archived: Filter<boolean>;
}

export interface IContractFilterProps {
  suppliers: TSupplier[];
  filterOptions: FilterOptions;
  isOpen: boolean;
}

export interface IContractFilterState {}

export default class ContractFilter extends React.Component<
  IContractFilterProps,
  IContractFilterState
> {
  constructor(props: IContractFilterProps) {
    super(props);

    this.state = {};
  }

  private renderDateField = (filter: Filter<Date | null>) => {
    return (
      <DatePicker_react
        onChange={(ev) => {
          filter.OnChange(ev);
        }}
        selected={filter.currentFilter ?? null}
        placeholderText="åååå-mm-dd"
      />
    );
  };

  private getSupplierLabel = (supplierOrgNr: string): string => {
    if (!supplierOrgNr) {
      return "";
    }

    const supplier = this.props.suppliers.find(
      (supp) => supp.supplierOrgNr === supplierOrgNr,
    );

    if (!supplier) {
      return "";
    }

    return `${supplier.supplierName} - ${supplier.supplierOrgNr}`;
  };

  public render() {
    const supplierCurrentFilter =
      this.props.filterOptions.supplier.currentFilter;

    const supplierSelectedLabel = this.getSupplierLabel(supplierCurrentFilter);

    const supplierOptions = this.props.suppliers.map(
      (supplier) => `${supplier.supplierName} - ${supplier.supplierOrgNr}`,
    );

    const categoryCurrentFilter =
      this.props.filterOptions.category.currentFilter;

    const termCurrentFilter = this.props.filterOptions.term.currentFilter;

    return (
      <Stack
        gap={1}
        aria-expanded={this.props.isOpen}
        className="contract-filter"
      >
        <div className="contract-filter-title">
          {t("contractListPage.filter.title")}
        </div>

        <Row>
          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("general.supplier")}
            </div>

            <Typeahead
              placeholder={t(
                "contractListPage.filter.supplierPlaceholder",
              ).toString()}
              id="supplier"
              selected={supplierSelectedLabel ? [supplierSelectedLabel] : []}
              options={supplierOptions}
              onChange={(selected) => {
                if (selected.length === 0) {
                  this.props.filterOptions.supplier.OnChange("");
                  return;
                }

                const supplierLabel = selected[0].toString();

                const foundSupplier = this.props.suppliers.find(
                  (supp) =>
                    `${supp.supplierName} - ${supp.supplierOrgNr}` ===
                    supplierLabel,
                );

                this.props.filterOptions.supplier.OnChange(
                  foundSupplier?.supplierOrgNr ?? "",
                );
              }}
            />
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("general.category")}
            </div>

            <Typeahead
              placeholder={t(
                "contractListPage.filter.categoryPlaceholder",
              ).toString()}
              id="category"
              selected={categoryCurrentFilter ? [categoryCurrentFilter] : []}
              options={this.props.filterOptions.category.options ?? []}
              onChange={(selected) => {
                if (selected.length === 0) {
                  this.props.filterOptions.category.OnChange("");
                  return;
                }

                const newVal = selected[0];

                this.props.filterOptions.category.OnChange(
                  newVal?.toString() ?? "",
                );
              }}
            />
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.list.name")}
            </div>

            <Typeahead
              placeholder={t(
                "contractListPage.filter.categoryPlaceholder",
              ).toString()}
              id="term"
              selected={termCurrentFilter ? [termCurrentFilter] : []}
              options={this.props.filterOptions.term.options ?? []}
              onChange={(selected) => {
                if (selected.length === 0) {
                  this.props.filterOptions.term.OnChange("");
                  return;
                }

                const newVal = selected[0];

                this.props.filterOptions.term.OnChange(
                  newVal?.toString() ?? "",
                );
              }}
            />
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.startDateBegin")}
            </div>

            {this.renderDateField(this.props.filterOptions.startDateBegin)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.startDateEnd")}
            </div>

            {this.renderDateField(this.props.filterOptions.startDateEnd)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.startReportDateBegin")}
            </div>

            {this.renderDateField(
              this.props.filterOptions.startReportDateBegin,
            )}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.startReportDateEnd")}
            </div>

            {this.renderDateField(this.props.filterOptions.startReportDateEnd)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.endReportDateBegin")}
            </div>

            {this.renderDateField(this.props.filterOptions.endReportDateBegin)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.endReportDateEnd")}
            </div>

            {this.renderDateField(this.props.filterOptions.endReportDateEnd)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.terminateDateBegin")}
            </div>

            {this.renderDateField(this.props.filterOptions.terminateDateBegin)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("contractListPage.filter.terminateDateEnd")}
            </div>

            {this.renderDateField(this.props.filterOptions.terminateDateEnd)}
          </Col>

          <Col xs={12} sm={6} md={6} lg={6}>
            <div className="contract-filter-field-label">
              {t("general.archived")}
            </div>

            <StyledCheckbox
              isChecked={this.props.filterOptions.archived.currentFilter}
              label=""
              onChange={(newVal) =>
                this.props.filterOptions.archived.OnChange(newVal)
              }
            />
          </Col>
        </Row>
      </Stack>
    );
  }
}
